import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HTTP_INTERCEPTORS } from '@angular/common/http';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { OauthService } from './oauth.service';
import { environment } from '../../environments/environment';

const RETRY_HEADER = 'X-Auth-Retry';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private oauth: OauthService) { }

  /** Attach the bearer token to requests aimed at our backends. */
  private withAuth(req: HttpRequest<unknown>, tokenOverride?: string): HttpRequest<unknown> {
    let headers = req.headers;
    const url = req.url;
    const token = tokenOverride || sessionStorage.getItem('userAccessToken') || this.oauth.user?.access_token;

    if (url.startsWith(environment.backendUrl) || url.startsWith(environment.userMcpMicroserviceUrl)) {
      if (token) {
        headers = headers.set('Authorization', `Bearer ${token}`);
      }
    }

    const currentRepoUrl = sessionStorage.getItem('currentRepoUrl');
    if (currentRepoUrl && url.startsWith(currentRepoUrl)) {
      headers = headers.set('Authorization', `Bearer ${token || ''}`);
    }

    return req.clone({ headers });
  }

  /** Only refresh-and-retry our own API calls, never the /auth/* endpoints. */
  private isRefreshable(req: HttpRequest<unknown>): boolean {
    const url = req.url;
    const currentRepoUrl = sessionStorage.getItem('currentRepoUrl');
    const isOurApi =
      url.startsWith(environment.backendUrl) ||
      url.startsWith(environment.userMcpMicroserviceUrl) ||
      (!!currentRepoUrl && url.startsWith(currentRepoUrl));
    return isOurApi && !url.includes('/auth/');
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const authReq = this.withAuth(req);

    return next.handle(authReq).pipe(
      catchError((error: unknown) => {
        const alreadyRetried = req.headers.has(RETRY_HEADER);
        if (
          error instanceof HttpErrorResponse &&
          error.status === 401 &&
          this.isRefreshable(req) &&
          !alreadyRetried
        ) {
          // Access token likely expired mid-session — refresh once and replay.
          return from(this.oauth.refreshSession()).pipe(
            switchMap((user) => {
              if (!user) {
                return throwError(() => error);
              }
              const retried = this.withAuth(
                req.clone({ setHeaders: { [RETRY_HEADER]: '1' } }),
                user.access_token,
              );
              return next.handle(retried);
            }),
          );
        }
        return throwError(() => error);
      }),
    );
  }
}

// Provider for app.config.ts
export const authInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: AuthInterceptor,
  multi: true,
};
